import React from 'react';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Stack,
  Link as MuiLink,
  Divider,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { FaInstagram, FaTelegramPlane, FaVk } from 'react-icons/fa';

const Footer = () => {
  const navigate = useNavigate();

  const handleNavigation = (to) => {
    if (to.startsWith('/#')) {
      // Если это якорь на главной
      if (window.location.pathname === '/') {
        const element = document.getElementById(to.replace('/#', ''));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(to.replace('/#', ''));
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 300);
      }
    } else {
      navigate(to);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrivacyClick = (e) => {
    e.preventDefault();
    navigate('/privacy');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  return (
    <Box
      component="footer"
      sx={{
        mt: 8,
        pt: { xs: 5, md: 6 },
        pb: 3,
        position: 'relative',
        overflow: 'hidden',
        background: `
          radial-gradient(circle at 12% 20%, rgba(201,161,74,0.12) 0%, transparent 24%),
          radial-gradient(circle at 88% 82%, rgba(20,36,95,0.12) 0%, transparent 28%),
          linear-gradient(180deg, #e9e9e7 0%, #e3e3e0 100%)
        `,
        color: '#14245f',
        borderTop: '1px solid rgba(20,36,95,0.08)',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background:
            'linear-gradient(135deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0) 38%, rgba(20,36,95,0.03) 100%)',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'stretch' },
            justifyContent: 'space-between',
            gap: { xs: 4, md: 6 },
            mb: 3.5,
          }}
        >
          <Box sx={{ maxWidth: 360 }}>
            <Typography
              variant="h5"
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 700,
                letterSpacing: 1,
                mb: 1,
                color: '#14245f',
              }}
            >
              LINDOS BEAUTY
            </Typography>

            <Typography
              sx={{
                fontSize: 14,
                lineHeight: 1.75,
                color: '#5c6685',
                mb: 2,
              }}
            >
              Студия красоты с эстетичной атмосферой, сильными мастерами и
              вниманием к каждой детали.
            </Typography>

            <Stack direction="row" spacing={1}>
              {[
                { icon: <FaInstagram size={18} />, href: 'https://instagram.com/lindos_beauty', label: 'Instagram' },
                { icon: <FaVk size={18} />, href: 'https://vk.com/lindos_beauty_samara', label: 'VK' },
                { icon: <FaTelegramPlane size={18} />, href: 'https://t.me/lindos_beauty', label: 'Telegram' },
              ].map((item) => (
                <IconButton
                  key={item.label}
                  aria-label={item.label}
                  href={item.href}
                  target="_blank"
                  rel="noreferrer"
                  sx={{
                    width: 42,
                    height: 42,
                    color: '#14245f',
                    background: 'rgba(255,255,255,0.72)',
                    border: '1px solid rgba(20,36,95,0.08)',
                    boxShadow: '0 8px 18px rgba(0,0,0,0.04)',
                    '&:hover': {
                      background:
                        'linear-gradient(135deg, rgba(201,161,74,0.18) 0%, rgba(255,255,255,0.95) 100%)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.25s ease',
                  }}
                >
                  {item.icon}
                </IconButton>
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 800,
                letterSpacing: '0.08em',
                color: '#b2872f',
                mb: 1.3,
              }}
            >
              НАВИГАЦИЯ
            </Typography>

            <Stack spacing={1}>
              {[
                { label: 'Главная', to: '/' },
                { label: 'Услуги', to: '/#services' },
                { label: 'Контакты', to: '/contacts' },
              ].map((item) => (
                <MuiLink
                  key={item.to}
                  component="button"
                  onClick={() => handleNavigation(item.to)}
                  underline="none"
                  sx={{
                    color: '#14245f',
                    fontSize: 14,
                    fontWeight: 600,
                    width: 'fit-content',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    textAlign: 'left',
                    '&:hover': {
                      color: '#b2872f',
                    },
                  }}
                >
                  {item.label}
                </MuiLink>
              ))}
            </Stack>
          </Box>

          <Box sx={{ textAlign: { xs: 'left', md: 'right' }, maxWidth: 340 }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 800,
                letterSpacing: '0.08em',
                color: '#b2872f',
                mb: 1.3,
              }}
            >
              КОНТАКТЫ
            </Typography>

            <Typography sx={{ fontSize: 14, mb: 0.6, color: '#4b556f' }}>
              Самара, ул. Революционная, 3
            </Typography>

            <Typography sx={{ fontSize: 14, mb: 0.6, color: '#4b556f' }}>
              Самара, ул. Дачная, 27А
            </Typography>

            <Typography sx={{ fontSize: 14, mb: 0.9 }}>
              Тел:{' '}
              <MuiLink
                href="tel:+79170300308"
                sx={{
                  color: '#14245f',
                  textDecoration: 'none',
                  fontWeight: 700,
                }}
              >
                +7 (917) 030-03-08
              </MuiLink>
            </Typography>

            <Typography sx={{ fontSize: 13, color: '#667085' }}>
              Ежедневно с 10:00 до 21:00
            </Typography>

            <MuiLink
              component="button"
              onClick={handlePrivacyClick}
              underline="none"
              sx={{
                display: 'inline-block',
                mt: 1.5,
                fontSize: 12,
                color: '#667085',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'inherit',
                '&:hover': {
                  color: '#b2872f',
                },
              }}
            >
              Политика конфиденциальности
            </MuiLink>
          </Box>
        </Box>

        <Divider sx={{ borderColor: 'rgba(20,36,95,0.12)', mb: 2 }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: 12, color: '#667085' }}>
            © {new Date().getFullYear()} Lindos Beauty.
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#667085' }}>
            Красота, стиль и забота о деталях.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;